import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowsClockwise,
  ArrowRight,
  Buildings,
  CalendarBlank,
  CheckCircle,
  GearSix,
  Info,
  Plus,
  ShieldCheck,
  Trash,
  TrendUp,
  UsersThree,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  WEEKDAYS,
  createChildScheduleWindow,
  createLocation,
  createRatioPolicySet,
  createRatioRule,
  createShiftBreak,
  deleteChildScheduleWindow,
  deleteRatioRule,
  deleteShiftBreak,
  fetchRatioCapacityForecast,
  fetchRatioCapacitySetup,
  fetchRatioCapacitySnapshot,
  setAssignmentRatioDesignation,
  setRatioPolicyActive,
  updateClassroomRatioConfig,
} from "@/lib/ratioCapacity";

const TABS = [
  { key: "live", label: "Live coverage", icon: TrendUp },
  { key: "forecast", label: "Forecast", icon: CalendarBlank },
  { key: "setup", label: "Setup", icon: GearSix },
];

const STATUS_META = {
  within_configured_ratio: {
    label: "Configured coverage met",
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  no_children: {
    label: "No children checked in",
    className: "border-neutral-200 bg-neutral-100 text-neutral-600",
  },
  no_scheduled_children: {
    label: "No children scheduled",
    className: "border-neutral-200 bg-neutral-100 text-neutral-600",
  },
  needs_configuration: {
    label: "Setup needed",
    className: "border-amber-200 bg-amber-50 text-amber-900",
  },
  needs_review: {
    label: "Review data",
    className: "border-amber-200 bg-amber-50 text-amber-900",
  },
  capacity_exceeded: {
    label: "Capacity exceeded",
    className: "border-red-200 bg-red-50 text-red-800",
  },
  out_of_ratio: {
    label: "Coverage gap",
    className: "border-red-200 bg-red-50 text-red-800",
  },
};

const REASON_LABELS = {
  missing_ratio_policy: "No active ratio policy for this location.",
  missing_licensed_capacity: "Licensed capacity has not been configured.",
  child_missing_date_of_birth: "A present child is missing a date of birth.",
  child_without_matching_age_rule: "A child does not match an age rule.",
  attendance_missing_check_in_time: "A present attendance record has no check-in time.",
  staff_clocked_in_multiple_classrooms: "A staff member is clocked into more than one classroom, so coverage needs review.",
  mixed_age_requires_review: "Mixed ages need a configured handling method or human review.",
  licensed_capacity_exceeded: "The configured licensed capacity is exceeded.",
  configured_group_size_exceeded: "The configured maximum group size is exceeded.",
  qualified_staff_gap: "Additional designated staff are needed for the configured ratio.",
  clocked_staff_not_designated_for_ratio: "Some clocked-in staff are not designated to count toward the ratio.",
  scheduled_child_missing_date_of_birth: "A scheduled child is missing a date of birth.",
  scheduled_child_without_matching_age_rule: "A scheduled child does not match an age rule.",
  staff_scheduled_in_multiple_classrooms: "A staff member is scheduled in more than one classroom at the same time, so coverage needs review.",
  scheduled_staff_not_designated_for_ratio: "Some scheduled staff are not designated to count toward the ratio.",
};

const inputClass =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-neutral-50";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function tomorrowIso() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

function dateTimeInputValue(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function statusMeta(status) {
  return STATUS_META[status] || {
    label: "Needs review",
    className: "border-amber-200 bg-amber-50 text-amber-900",
  };
}

function StatusChip({ status }) {
  const meta = statusMeta(status);
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function EmptyPanel({ icon: Icon = Info, title, children, action }) {
  return (
    <section className="rounded-2xl border border-dashed border-neutral-300 bg-white p-7 text-center">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary-hover">
        <Icon size={22} weight="duotone" aria-hidden />
      </span>
      <h2 className="mt-3 font-heading text-lg font-semibold tracking-tight text-neutral-900">{title}</h2>
      <p className="mx-auto mt-1 max-w-xl text-sm leading-6 text-neutral-600">{children}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </section>
  );
}

function LoadingPanel() {
  return (
    <div className="flex items-center justify-center py-24" aria-label="Loading ratio and capacity">
      <div className="h-9 w-9 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
    </div>
  );
}

export default function AdminRatioCapacity() {
  const { profile } = useAuth();
  const [tab, setTab] = useState("live");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [snapshot, setSnapshot] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [forecastDate, setForecastDate] = useState(tomorrowIso());

  const load = useCallback(
    async ({ background = false } = {}) => {
      if (background) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const locationId = selectedLocationId || null;
        const [liveRows, forecastRows, setupData] = await Promise.all([
          fetchRatioCapacitySnapshot(locationId),
          fetchRatioCapacityForecast({ date: forecastDate, locationId }),
          fetchRatioCapacitySetup(),
        ]);
        setSnapshot(liveRows);
        setForecast(forecastRows);
        setSetup(setupData);
      } catch (loadError) {
        setError(loadError.message || "HoneyAcorn could not load ratio and capacity data.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [forecastDate, selectedLocationId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const locations = setup?.locations || [];
  const visibleSnapshot = snapshot;
  const visibleForecast = forecast;
  const summary = useMemo(() => {
    const concerns = visibleSnapshot.filter((row) =>
      ["out_of_ratio", "capacity_exceeded", "needs_configuration", "needs_review"].includes(row.out_status),
    );
    return {
      classrooms: visibleSnapshot.length,
      configured: visibleSnapshot.filter((row) => row.out_status === "within_configured_ratio").length,
      attention: concerns.length,
      capacityUsed: visibleSnapshot.reduce((total, row) => total + (row.out_children_present || 0), 0),
    };
  }, [visibleSnapshot]);

  const runMutation = async (message, operation) => {
    try {
      await operation();
      toast.success(message);
      await load({ background: true });
      return true;
    } catch (mutationError) {
      toast.error(mutationError.message || "The change could not be saved.");
      return false;
    }
  };

  if (loading) return <LoadingPanel />;

  if (error) {
    return (
      <div data-testid="admin-ratio-capacity-error" className="space-y-5">
        <EmptyPanel
          icon={WarningCircle}
          title="Ratio & capacity is unavailable"
          action={
            <button type="button" onClick={() => load()} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover">
              Try again
            </button>
          }
        >
          {error} The app has not inferred any compliance result.
        </EmptyPanel>
      </div>
    );
  }

  return (
    <div data-testid="admin-ratio-capacity" className="space-y-6">
      <section className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Operational coverage</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Ratio & capacity
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-neutral-600">
            See real attendance against your configured rules, then plan coverage before the day starts. HoneyAcorn surfaces missing data instead of assuming regulatory compliance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="ratio-location-filter">Location</label>
          <select
            id="ratio-location-filter"
            data-testid="admin-ratio-capacity-location-filter"
            value={selectedLocationId}
            onChange={(event) => setSelectedLocationId(event.target.value)}
            className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">All locations</option>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
          <button
            type="button"
            data-testid="admin-ratio-capacity-refresh"
            onClick={() => load({ background: true })}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3.5 py-2 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-60"
          >
            <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </section>

      <section aria-label="Ratio and capacity views" className="inline-flex max-w-full overflow-x-auto rounded-full border border-neutral-200 bg-white p-1">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            data-testid={`admin-ratio-capacity-tab-${key}`}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition ${
              tab === key ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            <Icon size={15} weight="duotone" />
            {label}
          </button>
        ))}
      </section>

      {tab === "live" ? (
        <LiveCoverage
          rows={visibleSnapshot}
          summary={summary}
          onOpenSetup={() => setTab("setup")}
          onOpenForecast={() => setTab("forecast")}
        />
      ) : null}
      {tab === "forecast" ? (
        <Forecast
          rows={visibleForecast}
          date={forecastDate}
          setDate={setForecastDate}
          onOpenSetup={() => setTab("setup")}
        />
      ) : null}
      {tab === "setup" ? (
        <SetupWorkspace
          setup={setup}
          organizationId={profile?.organization_id}
          actorProfileId={profile?.id}
          onMutate={runMutation}
        />
      ) : null}
    </div>
  );
}

function LiveCoverage({ rows, summary, onOpenSetup, onOpenForecast }) {
  return (
    <div className="space-y-5">
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Classrooms in view" value={summary.classrooms} icon={Buildings} />
        <Metric label="Configured coverage met" value={summary.configured} icon={CheckCircle} tone="good" />
        <Metric label="Need attention" value={summary.attention} icon={WarningCircle} tone={summary.attention ? "warn" : "good"} />
        <Metric label="Children checked in" value={summary.capacityUsed} icon={UsersThree} />
      </section>

      <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4 sm:flex sm:items-center sm:justify-between sm:gap-6">
        <div className="flex gap-3">
          <Info size={19} weight="duotone" className="mt-0.5 shrink-0 text-primary-hover" />
          <p className="text-sm leading-6 text-neutral-700">
            Live coverage uses checked-in children and clocked-in staff. Planned shifts belong in the forecast, not in the live count.
          </p>
        </div>
        <button type="button" onClick={onOpenForecast} className="mt-3 inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary-hover hover:underline sm:mt-0">
          View forecast <ArrowRight size={14} weight="bold" />
        </button>
      </section>

      {rows.length === 0 ? (
        <EmptyPanel
          icon={Buildings}
          title="No classrooms are available in this view"
          action={<button type="button" onClick={onOpenSetup} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover">Open setup</button>}
        >
          Add a location and classroom, then configure its licensed capacity and applicable ratio policy.
        </EmptyPanel>
      ) : (
        <section className="grid gap-4 xl:grid-cols-2">
          {rows.map((row) => <LiveClassroomCard key={row.out_classroom_id} row={row} onOpenSetup={onOpenSetup} />)}
        </section>
      )}
    </div>
  );
}

function Metric({ label, value, icon: Icon, tone = "neutral" }) {
  const toneClass = tone === "good" ? "border-emerald-200" : tone === "warn" ? "border-amber-200" : "border-neutral-200";
  return (
    <article className={`rounded-2xl border bg-white p-4 ${toneClass}`}>
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary-hover"><Icon size={18} weight="duotone" /></span>
      <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.13em] text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900">{value}</p>
    </article>
  );
}

function LiveClassroomCard({ row, onOpenSetup }) {
  const reasonCodes = row.out_reason_codes || [];
  const isAttention = ["out_of_ratio", "capacity_exceeded", "needs_configuration", "needs_review"].includes(row.out_status);
  const staffText = row.out_required_staff == null
    ? "Waiting for configuration"
    : `${row.out_qualified_staff_present} of ${row.out_required_staff} designated staff`;

  return (
    <article data-testid={`admin-ratio-capacity-classroom-${row.out_classroom_id}`} className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.13em] text-neutral-500">{row.out_location_name}</p>
          <h2 className="mt-1 font-heading text-xl font-semibold tracking-tight text-neutral-900">{row.out_classroom_name}</h2>
        </div>
        <StatusChip status={row.out_status} />
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-3 border-y border-neutral-100 py-4 text-sm sm:grid-cols-4">
        <Readout label="Children here" value={row.out_children_present} />
        <Readout label="Designated staff" value={row.out_qualified_staff_present} />
        <Readout label="Needed" value={row.out_required_staff ?? "—"} emphasis={isAttention} />
        <Readout label="Licensed cap." value={row.out_licensed_capacity ?? "—"} />
      </dl>

      <p className="mt-4 text-sm font-medium text-neutral-800">{staffText}</p>
      {row.out_licensed_capacity != null ? (
        <p className="mt-1 text-xs text-neutral-500">
          Capacity: {row.out_children_present} / {row.out_licensed_capacity}
          {row.out_capacity_utilization != null ? ` (${Math.round(Number(row.out_capacity_utilization) * 100)}%)` : ""}
        </p>
      ) : null}

      {reasonCodes.length ? (
        <ul className="mt-4 space-y-2 rounded-xl bg-neutral-50 p-3 text-xs leading-5 text-neutral-700">
          {reasonCodes.map((reason) => (
            <li key={reason} className="flex gap-2"><WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />{REASON_LABELS[reason] || reason}</li>
          ))}
        </ul>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-neutral-500">
          {row.out_rule_summary?.policy_name ? `Policy: ${row.out_rule_summary.policy_name}` : "No active policy selected"}
        </p>
        {isAttention ? (
          <button type="button" onClick={onOpenSetup} className="inline-flex items-center gap-1 text-sm font-semibold text-primary-hover hover:underline">
            Resolve setup <ArrowRight size={14} weight="bold" />
          </button>
        ) : null}
      </div>
    </article>
  );
}

function Readout({ label, value, emphasis }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-500">{label}</dt>
      <dd className={`mt-1 font-heading text-lg font-semibold ${emphasis ? "text-amber-800" : "text-neutral-900"}`}>{value}</dd>
    </div>
  );
}

function Forecast({ rows, date, setDate, onOpenSetup }) {
  const actionable = rows.filter((row) => ["out_of_ratio", "capacity_exceeded", "needs_configuration", "needs_review"].includes(row.out_status));
  return (
    <div className="space-y-5">
      <section className="flex flex-col justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-4 sm:flex-row sm:items-center">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">Projected coverage</h2>
          <p className="mt-1 text-sm text-neutral-600">Built from configured child attendance windows, staff shifts, and scheduled breaks.</p>
        </div>
        <label className="text-sm font-semibold text-neutral-700">
          <span className="sr-only">Forecast date</span>
          <input data-testid="admin-ratio-capacity-forecast-date" type="date" value={date} min={todayIso()} onChange={(event) => setDate(event.target.value)} className={inputClass} />
        </label>
      </section>

      {actionable.length > 0 ? (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex gap-3">
            <WarningCircle size={20} weight="duotone" className="mt-0.5 shrink-0 text-amber-700" />
            <div>
              <h2 className="font-heading text-base font-semibold text-amber-950">{actionable.length} projected interval{actionable.length === 1 ? "" : "s"} needs attention</h2>
              <p className="mt-1 text-sm leading-6 text-amber-900">Open setup to add missing schedules, licensed capacity, policies, or qualified-staff designations.</p>
            </div>
          </div>
        </section>
      ) : null}

      {rows.length === 0 ? (
        <EmptyPanel
          icon={CalendarBlank}
          title="No projected coverage intervals yet"
          action={<button type="button" onClick={onOpenSetup} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover">Configure planning inputs</button>}
        >
          Add at least one child attendance window or staff shift to create a forecast. HoneyAcorn will not fabricate a schedule from a tuition pattern.
        </EmptyPanel>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
          <div className="overflow-x-auto">
            <table data-testid="admin-ratio-capacity-forecast-table" className="min-w-[760px] w-full text-left text-sm">
              <thead className="bg-neutral-50 text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500">
                <tr><th className="px-4 py-3">Classroom</th><th className="px-4 py-3">Time</th><th className="px-4 py-3">Scheduled</th><th className="px-4 py-3">Designated staff</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.out_classroom_id}-${row.out_starts_at}-${row.out_ends_at}`} className="border-t border-neutral-100 align-top">
                    <td className="px-4 py-3"><p className="font-semibold text-neutral-900">{row.out_classroom_name}</p><p className="text-xs text-neutral-500">{row.out_location_name}</p></td>
                    <td className="px-4 py-3 text-neutral-700">{formatTime(row.out_starts_at)} – {formatTime(row.out_ends_at)}</td>
                    <td className="px-4 py-3 text-neutral-700">{row.out_scheduled_children} child{row.out_scheduled_children === 1 ? "" : "ren"}<span className="text-neutral-400"> / {row.out_licensed_capacity ?? "—"} cap.</span></td>
                    <td className="px-4 py-3 text-neutral-700">{row.out_qualified_staff_scheduled} / {row.out_required_staff ?? "—"}</td>
                    <td className="px-4 py-3"><StatusChip status={row.out_status} /><ReasonSummary reasons={row.out_reason_codes} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function formatTime(value) {
  if (!value) return "—";
  const [hoursRaw, minutes = "00"] = String(value).slice(0, 5).split(":");
  const hours = Number(hoursRaw);
  if (!Number.isFinite(hours)) return value;
  const suffix = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes} ${suffix}`;
}

function ReasonSummary({ reasons = [] }) {
  if (!reasons.length) return null;
  return <p className="mt-2 max-w-xs text-xs leading-5 text-neutral-600">{reasons.map((reason) => REASON_LABELS[reason] || reason).join(" ")}</p>;
}

function SetupWorkspace({ setup, organizationId, actorProfileId, onMutate }) {
  const [locationForm, setLocationForm] = useState({ name: "", code: "", address: "" });
  const [policyForm, setPolicyForm] = useState({ locationId: setup.locations[0]?.id || "", name: "", jurisdictionLabel: "", sourceReference: "", effectiveOn: todayIso(), mixedAgeMode: "review_required" });
  const [ruleForm, setRuleForm] = useState({ policySetId: setup.policySets[0]?.id || "", label: "", minAgeMonths: "", maxAgeMonths: "", childrenPerQualifiedStaff: "", maxGroupSize: "" });
  const [scheduleForm, setScheduleForm] = useState({ childId: setup.children.find((child) => child.classroom_id)?.id || "", weekday: 1, startsAt: "08:00", endsAt: "17:00", effectiveOn: todayIso(), endsOn: "" });
  const [breakForm, setBreakForm] = useState({ shiftId: setup.shifts[0]?.id || "", startsAt: setup.shifts[0] ? dateTimeInputValue(setup.shifts[0].starts_at) : "", endsAt: setup.shifts[0] ? dateTimeInputValue(setup.shifts[0].ends_at) : "" });
  const [busy, setBusy] = useState("");

  const policyById = useMemo(() => Object.fromEntries(setup.policySets.map((item) => [item.id, item])), [setup.policySets]);
  const childById = useMemo(() => Object.fromEntries(setup.children.map((item) => [item.id, item])), [setup.children]);
  const breakByShift = useMemo(() => {
    const groups = {};
    setup.breaks.forEach((item) => { groups[item.staff_shift_id] = [...(groups[item.staff_shift_id] || []), item]; });
    return groups;
  }, [setup.breaks]);

  const mutate = async (key, message, operation) => {
    setBusy(key);
    try {
      await onMutate(message, operation);
    } finally {
      setBusy("");
    }
  };

  const createLocationSubmit = async (event) => {
    event.preventDefault();
    if (!locationForm.name.trim()) return toast.error("Give the location a name.");
    await mutate("location", "Location created", async () => {
      await createLocation({ organizationId, ...locationForm });
      setLocationForm({ name: "", code: "", address: "" });
    });
  };

  const createPolicySubmit = async (event) => {
    event.preventDefault();
    if (!policyForm.locationId || !policyForm.name.trim()) return toast.error("Choose a location and name the policy.");
    await mutate("policy", "Ratio policy added", async () => {
      await createRatioPolicySet({ organizationId, actorProfileId, ...policyForm });
      setPolicyForm((previous) => ({ ...previous, name: "", jurisdictionLabel: "", sourceReference: "" }));
    });
  };

  const createRuleSubmit = async (event) => {
    event.preventDefault();
    const policy = policyById[ruleForm.policySetId];
    if (!policy || !ruleForm.label.trim() || ruleForm.minAgeMonths === "" || ruleForm.maxAgeMonths === "" || ruleForm.childrenPerQualifiedStaff === "") {
      return toast.error("Complete the policy, age range, and ratio.");
    }
    if (Number(ruleForm.maxAgeMonths) < Number(ruleForm.minAgeMonths)) return toast.error("The maximum age must be at least the minimum age.");
    await mutate("rule", "Age rule added", async () => {
      await createRatioRule({ organizationId, locationId: policy.location_id, ...ruleForm });
      setRuleForm((previous) => ({ ...previous, label: "", minAgeMonths: "", maxAgeMonths: "", childrenPerQualifiedStaff: "", maxGroupSize: "" }));
    });
  };

  const createScheduleSubmit = async (event) => {
    event.preventDefault();
    const child = childById[scheduleForm.childId];
    if (!child?.classroom_id) return toast.error("Choose an active child who is assigned to a classroom.");
    if (scheduleForm.endsAt <= scheduleForm.startsAt) return toast.error("The schedule must end after it starts.");
    await mutate("schedule", "Child attendance window added", async () => {
      await createChildScheduleWindow({
        organizationId,
        actorProfileId,
        childId: child.id,
        classroomId: child.classroom_id,
        weekday: scheduleForm.weekday,
        startsAt: scheduleForm.startsAt,
        endsAt: scheduleForm.endsAt,
        effectiveOn: scheduleForm.effectiveOn,
        endsOn: scheduleForm.endsOn,
      });
    });
  };

  const createBreakSubmit = async (event) => {
    event.preventDefault();
    const shift = setup.shifts.find((item) => item.id === breakForm.shiftId);
    if (!shift || !breakForm.startsAt || !breakForm.endsAt) return toast.error("Choose a shift and break times.");
    const startsAt = new Date(breakForm.startsAt);
    const endsAt = new Date(breakForm.endsAt);
    if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime()) || endsAt <= startsAt) return toast.error("The break must end after it starts.");
    await mutate("break", "Scheduled break added", async () => {
      await createShiftBreak({ organizationId, actorProfileId, staffShiftId: shift.id, startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString() });
    });
  };

  return (
    <div data-testid="admin-ratio-capacity-setup" className="space-y-8">
      <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex gap-3">
          <ShieldCheck size={20} weight="duotone" className="mt-0.5 shrink-0 text-primary-hover" />
          <div>
            <h2 className="font-heading text-base font-semibold text-neutral-900">Safe by configuration</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-neutral-700">Rules, licensed capacity, staff designations, schedules, and breaks are configured here. HoneyAcorn keeps a configuration audit trail and marks incomplete data for review instead of silently calculating a legal determination.</p>
          </div>
        </div>
      </section>

      <SetupSection index="1" title="Locations" description="Each organization tenant has a default location. Add a separate operating location only when the center actually needs one.">
        <div className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
          <ul className="space-y-2">
            {setup.locations.map((location) => (
              <li key={location.id} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white p-3">
                <div><p className="font-semibold text-neutral-900">{location.name}</p><p className="text-xs text-neutral-500">{location.code || "No location code"}{location.is_default ? " · Default" : ""}</p></div>
                <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${location.active ? "bg-emerald-50 text-emerald-800" : "bg-neutral-100 text-neutral-500"}`}>{location.active ? "Active" : "Inactive"}</span>
              </li>
            ))}
          </ul>
          <form onSubmit={createLocationSubmit} className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-4">
            <p className="font-semibold text-neutral-900">Add location</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2"><input aria-label="Location name" value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} placeholder="North campus" className={inputClass} /><input aria-label="Location code" value={locationForm.code} onChange={(e) => setLocationForm({ ...locationForm, code: e.target.value })} placeholder="Optional code" className={inputClass} /></div>
            <input aria-label="Location address" value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} placeholder="Optional address" className={`${inputClass} mt-3`} />
            <button disabled={busy === "location"} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"><Plus size={14} weight="bold" />{busy === "location" ? "Adding…" : "Add location"}</button>
          </form>
        </div>
      </SetupSection>

      <SetupSection index="2" title="Classroom capacity" description="Licensed capacity is intentionally separate from the existing operational planning capacity. Leave it blank only when a director should be prompted to finish setup.">
        <div className="grid gap-3 xl:grid-cols-2">
          {setup.classrooms.map((classroom) => <ClassroomConfigRow key={classroom.id} classroom={classroom} locations={setup.locations} onSave={(patch) => mutate(`classroom-${classroom.id}`, "Classroom capacity updated", () => updateClassroomRatioConfig(classroom.id, patch))} busy={busy === `classroom-${classroom.id}`} />)}
          {!setup.classrooms.length ? <p className="rounded-xl border border-dashed border-neutral-300 p-5 text-sm text-neutral-600">Create a classroom from <Link to="/admin/classrooms" className="font-semibold text-primary-hover underline">Classrooms</Link> before configuring coverage.</p> : null}
        </div>
      </SetupSection>

      <SetupSection index="3" title="Applicable ratio policy" description="Capture the source that governs this location, then add non-overlapping age bands. No jurisdiction is hard-coded into HoneyAcorn.">
        <div className="grid gap-5 xl:grid-cols-[1fr_1.05fr]">
          <form onSubmit={createPolicySubmit} className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-4">
            <p className="font-semibold text-neutral-900">Add ratio policy</p>
            <div className="mt-3 grid gap-3">
              <select aria-label="Policy location" value={policyForm.locationId} onChange={(e) => setPolicyForm({ ...policyForm, locationId: e.target.value })} className={inputClass}><option value="">Choose location</option>{setup.locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select>
              <input aria-label="Policy name" value={policyForm.name} onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })} placeholder="Licensed care policy 2026" className={inputClass} />
              <input aria-label="Jurisdiction label" value={policyForm.jurisdictionLabel} onChange={(e) => setPolicyForm({ ...policyForm, jurisdictionLabel: e.target.value })} placeholder="Jurisdiction or program" className={inputClass} />
              <input aria-label="Source reference" value={policyForm.sourceReference} onChange={(e) => setPolicyForm({ ...policyForm, sourceReference: e.target.value })} placeholder="Source or policy reference" className={inputClass} />
              <div className="grid grid-cols-2 gap-3"><label className="text-xs font-semibold text-neutral-600">Effective on<input type="date" value={policyForm.effectiveOn} onChange={(e) => setPolicyForm({ ...policyForm, effectiveOn: e.target.value })} className={`${inputClass} mt-1`} /></label><label className="text-xs font-semibold text-neutral-600">Mixed ages<select value={policyForm.mixedAgeMode} onChange={(e) => setPolicyForm({ ...policyForm, mixedAgeMode: e.target.value })} className={`${inputClass} mt-1`}><option value="review_required">Require review</option><option value="strictest_present_age">Use strictest age</option></select></label></div>
            </div>
            <button disabled={busy === "policy"} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"><Plus size={14} weight="bold" />{busy === "policy" ? "Saving…" : "Add policy"}</button>
          </form>
          <div className="space-y-3">
            {setup.policySets.length ? setup.policySets.map((policy) => <PolicyCard key={policy.id} policy={policy} rules={setup.rules.filter((rule) => rule.policy_set_id === policy.id)} onToggle={() => mutate(`policy-${policy.id}`, policy.active ? "Policy paused" : "Policy activated", () => setRatioPolicyActive(policy.id, !policy.active))} onDeleteRule={(id) => mutate(`rule-delete-${id}`, "Age rule removed", () => deleteRatioRule(id))} busy={busy} />) : <p className="rounded-xl border border-dashed border-neutral-300 p-5 text-sm text-neutral-600">No ratio policy has been configured yet.</p>}
          </div>
        </div>
        <form onSubmit={createRuleSubmit} className="mt-5 rounded-xl border border-neutral-200 bg-white p-4">
          <p className="font-semibold text-neutral-900">Add age rule</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <select aria-label="Policy for age rule" value={ruleForm.policySetId} onChange={(e) => setRuleForm({ ...ruleForm, policySetId: e.target.value })} className={inputClass}><option value="">Choose policy</option>{setup.policySets.map((policy) => <option key={policy.id} value={policy.id}>{policy.name}</option>)}</select>
            <input aria-label="Age rule label" value={ruleForm.label} onChange={(e) => setRuleForm({ ...ruleForm, label: e.target.value })} placeholder="Infants" className={inputClass} />
            <input aria-label="Minimum age in months" type="number" min="0" max="240" value={ruleForm.minAgeMonths} onChange={(e) => setRuleForm({ ...ruleForm, minAgeMonths: e.target.value })} placeholder="Min months" className={inputClass} />
            <input aria-label="Maximum age in months" type="number" min="0" max="240" value={ruleForm.maxAgeMonths} onChange={(e) => setRuleForm({ ...ruleForm, maxAgeMonths: e.target.value })} placeholder="Max months" className={inputClass} />
            <input aria-label="Children per qualified staff" type="number" min="1" max="100" value={ruleForm.childrenPerQualifiedStaff} onChange={(e) => setRuleForm({ ...ruleForm, childrenPerQualifiedStaff: e.target.value })} placeholder="1 staff per N" className={inputClass} />
            <input aria-label="Maximum group size" type="number" min="1" max="500" value={ruleForm.maxGroupSize} onChange={(e) => setRuleForm({ ...ruleForm, maxGroupSize: e.target.value })} placeholder="Max group (optional)" className={inputClass} />
          </div>
          <button disabled={busy === "rule"} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3.5 py-2 text-sm font-semibold text-primary-hover hover:bg-primary/10 disabled:opacity-60"><Plus size={14} weight="bold" />{busy === "rule" ? "Adding…" : "Add age rule"}</button>
        </form>
      </SetupSection>

      <SetupSection index="4" title="Qualified-staff designation" description="A staff assignment never counts automatically. Designate the assignments that are authorized to count toward the configured ratio.">
        {setup.assignments.length ? <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white"><div className="overflow-x-auto"><table className="min-w-[650px] w-full text-sm"><thead className="bg-neutral-50 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-500"><tr><th className="px-4 py-3">Staff</th><th className="px-4 py-3">Classroom</th><th className="px-4 py-3">Assignment</th><th className="px-4 py-3 text-right">Counts toward ratio</th></tr></thead><tbody>{setup.assignments.map((assignment) => <tr key={assignment.id} className="border-t border-neutral-100"><td className="px-4 py-3 font-semibold text-neutral-900">{assignment.profile?.full_name || assignment.profile?.email || "Staff member"}</td><td className="px-4 py-3 text-neutral-700">{assignment.classroom?.name || "—"}</td><td className="px-4 py-3 capitalize text-neutral-600">{String(assignment.position || "").replaceAll("_", " ") || "—"}</td><td className="px-4 py-3 text-right"><label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-neutral-700"><span>{assignment.counts_toward_ratio ? "Yes" : "No"}</span><input aria-label={`Mark ${assignment.profile?.full_name || "staff member"} as qualified`} type="checkbox" checked={Boolean(assignment.counts_toward_ratio)} disabled={busy === `assignment-${assignment.id}`} onChange={(e) => mutate(`assignment-${assignment.id}`, e.target.checked ? "Staff designation saved" : "Staff designation removed", () => setAssignmentRatioDesignation(assignment.id, e.target.checked))} className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary" /></label></td></tr>)}</tbody></table></div></div> : <p className="rounded-xl border border-dashed border-neutral-300 p-5 text-sm text-neutral-600">Assign staff to classrooms from <Link to="/admin/staff" className="font-semibold text-primary-hover underline">Staff</Link> before making a designation.</p>}
      </SetupSection>

      <SetupSection index="5" title="Planned child attendance" description="Tuition schedules do not predict time-of-day attendance. Add the windows you actually expect to use for coverage planning.">
        <div className="grid gap-5 xl:grid-cols-[1fr_1.1fr]">
          <form onSubmit={createScheduleSubmit} className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-4">
            <p className="font-semibold text-neutral-900">Add attendance window</p>
            <div className="mt-3 grid gap-3 md:grid-cols-2"><select aria-label="Child for planned attendance" value={scheduleForm.childId} onChange={(e) => setScheduleForm({ ...scheduleForm, childId: e.target.value })} className={inputClass}><option value="">Choose active child</option>{setup.children.filter((child) => child.classroom_id).map((child) => <option key={child.id} value={child.id}>{child.first_name} {child.last_name || ""} · {child.classroom?.name || "Classroom"}</option>)}</select><select aria-label="Weekday" value={scheduleForm.weekday} onChange={(e) => setScheduleForm({ ...scheduleForm, weekday: e.target.value })} className={inputClass}>{WEEKDAYS.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}</select><label className="text-xs font-semibold text-neutral-600">Starts<input type="time" value={scheduleForm.startsAt} onChange={(e) => setScheduleForm({ ...scheduleForm, startsAt: e.target.value })} className={`${inputClass} mt-1`} /></label><label className="text-xs font-semibold text-neutral-600">Ends<input type="time" value={scheduleForm.endsAt} onChange={(e) => setScheduleForm({ ...scheduleForm, endsAt: e.target.value })} className={`${inputClass} mt-1`} /></label><label className="text-xs font-semibold text-neutral-600">Effective on<input type="date" value={scheduleForm.effectiveOn} onChange={(e) => setScheduleForm({ ...scheduleForm, effectiveOn: e.target.value })} className={`${inputClass} mt-1`} /></label><label className="text-xs font-semibold text-neutral-600">Ends on (optional)<input type="date" value={scheduleForm.endsOn} onChange={(e) => setScheduleForm({ ...scheduleForm, endsOn: e.target.value })} className={`${inputClass} mt-1`} /></label></div>
            <button disabled={busy === "schedule"} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"><Plus size={14} weight="bold" />{busy === "schedule" ? "Adding…" : "Add attendance window"}</button>
          </form>
          <ScheduleList schedules={setup.schedules} onDelete={(id) => mutate(`schedule-delete-${id}`, "Attendance window removed", () => deleteChildScheduleWindow(id))} busy={busy} />
        </div>
      </SetupSection>

      <SetupSection index="6" title="Scheduled staff breaks" description="Breaks split a planned shift so a forecast does not count that staff member during the break window.">
        <div className="grid gap-5 xl:grid-cols-[1fr_1.1fr]">
          <form onSubmit={createBreakSubmit} className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-4">
            <p className="font-semibold text-neutral-900">Add break</p>
            <div className="mt-3 grid gap-3"><select aria-label="Shift for break" value={breakForm.shiftId} onChange={(e) => { const shift = setup.shifts.find((item) => item.id === e.target.value); setBreakForm({ shiftId: e.target.value, startsAt: shift ? dateTimeInputValue(shift.starts_at) : "", endsAt: shift ? dateTimeInputValue(shift.ends_at) : "" }); }} className={inputClass}><option value="">Choose a future shift</option>{setup.shifts.map((shift) => <option key={shift.id} value={shift.id}>{shift.profile?.full_name || "Staff"} · {shift.classroom?.name || "Classroom"} · {new Date(shift.starts_at).toLocaleString()}</option>)}</select><label className="text-xs font-semibold text-neutral-600">Break starts<input type="datetime-local" value={breakForm.startsAt} onChange={(e) => setBreakForm({ ...breakForm, startsAt: e.target.value })} className={`${inputClass} mt-1`} /></label><label className="text-xs font-semibold text-neutral-600">Break ends<input type="datetime-local" value={breakForm.endsAt} onChange={(e) => setBreakForm({ ...breakForm, endsAt: e.target.value })} className={`${inputClass} mt-1`} /></label></div>
            <button disabled={busy === "break"} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"><Plus size={14} weight="bold" />{busy === "break" ? "Adding…" : "Add break"}</button>
          </form>
          <ShiftBreakList shifts={setup.shifts} breakByShift={breakByShift} onDelete={(id) => mutate(`break-delete-${id}`, "Scheduled break removed", () => deleteShiftBreak(id))} busy={busy} />
        </div>
      </SetupSection>
    </div>
  );
}

function SetupSection({ index, title, description, children }) {
  return <section className="space-y-4"><div className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">{index}</span><div><h2 className="font-heading text-xl font-semibold tracking-tight text-neutral-900">{title}</h2><p className="mt-1 max-w-3xl text-sm leading-6 text-neutral-600">{description}</p></div></div>{children}</section>;
}

function ClassroomConfigRow({ classroom, locations, onSave, busy }) {
  const [locationId, setLocationId] = useState(classroom.location_id || "");
  const [licensedCapacity, setLicensedCapacity] = useState(classroom.licensed_capacity ?? "");
  return <article className="rounded-xl border border-neutral-200 bg-white p-4"><div className="flex flex-wrap items-start justify-between gap-3"><div><p className="font-semibold text-neutral-900">{classroom.name}</p><p className="text-xs text-neutral-500">Operational capacity: {classroom.capacity ?? "Not set"}</p></div><button type="button" disabled={busy} onClick={() => onSave({ locationId, licensedCapacity })} className="rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10 disabled:opacity-60">{busy ? "Saving…" : "Save"}</button></div><div className="mt-3 grid gap-3 sm:grid-cols-2"><label className="text-xs font-semibold text-neutral-600">Location<select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={`${inputClass} mt-1`}><option value="">Choose location</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label><label className="text-xs font-semibold text-neutral-600">Licensed capacity<input type="number" min="0" max="1000" value={licensedCapacity} onChange={(e) => setLicensedCapacity(e.target.value)} placeholder="Required for capacity check" className={`${inputClass} mt-1`} /></label></div></article>;
}

function PolicyCard({ policy, rules, onToggle, onDeleteRule, busy }) {
  return <article className="rounded-xl border border-neutral-200 bg-white p-4"><div className="flex flex-wrap items-start justify-between gap-3"><div><div className="flex flex-wrap items-center gap-2"><p className="font-semibold text-neutral-900">{policy.name}</p><span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${policy.active ? "bg-emerald-50 text-emerald-800" : "bg-neutral-100 text-neutral-500"}`}>{policy.active ? "Active" : "Paused"}</span></div><p className="mt-1 text-xs text-neutral-500">{policy.jurisdiction_label || "No jurisdiction label"} · effective {policy.effective_on}</p>{policy.source_reference ? <p className="mt-1 text-xs text-neutral-500">Source: {policy.source_reference}</p> : null}</div><button type="button" disabled={busy === `policy-${policy.id}`} onClick={onToggle} className="rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">{policy.active ? "Pause" : "Activate"}</button></div><p className="mt-3 text-xs text-neutral-600">Mixed ages: {policy.mixed_age_mode === "strictest_present_age" ? "uses strictest present age" : "requires review"}</p><ul className="mt-3 divide-y divide-neutral-100 rounded-lg border border-neutral-100">{rules.length ? rules.map((rule) => <li key={rule.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs"><span><strong className="text-neutral-800">{rule.label}</strong><span className="text-neutral-500"> · {rule.min_age_months}–{rule.max_age_months} mo · 1:{rule.children_per_qualified_staff}{rule.max_group_size ? ` · max ${rule.max_group_size}` : ""}</span></span><button type="button" aria-label={`Remove ${rule.label} rule`} disabled={busy === `rule-delete-${rule.id}`} onClick={() => onDeleteRule(rule.id)} className="rounded-full p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-700"><Trash size={14} weight="bold" /></button></li>) : <li className="px-3 py-3 text-xs text-neutral-500">No age rules yet.</li>}</ul></article>;
}

function ScheduleList({ schedules, onDelete, busy }) {
  return <div className="rounded-xl border border-neutral-200 bg-white p-4"><p className="font-semibold text-neutral-900">Configured windows</p>{schedules.length ? <ul className="mt-3 divide-y divide-neutral-100">{schedules.map((schedule) => <li key={schedule.id} className="flex items-center justify-between gap-3 py-2.5 text-sm"><div><p className="font-medium text-neutral-900">{schedule.child?.first_name || "Child"} {schedule.child?.last_name || ""}</p><p className="text-xs text-neutral-500">{WEEKDAYS.find((day) => Number(day.value) === Number(schedule.weekday))?.label} · {formatTime(schedule.starts_at)}–{formatTime(schedule.ends_at)} · {schedule.classroom?.name || "Classroom"}</p></div><button type="button" aria-label="Remove attendance window" disabled={busy === `schedule-delete-${schedule.id}`} onClick={() => onDelete(schedule.id)} className="rounded-full p-2 text-neutral-500 hover:bg-red-50 hover:text-red-700"><Trash size={15} weight="bold" /></button></li>)}</ul> : <p className="mt-3 text-sm text-neutral-500">No attendance windows configured.</p>}</div>;
}

function ShiftBreakList({ shifts, breakByShift, onDelete, busy }) {
  return <div className="rounded-xl border border-neutral-200 bg-white p-4"><p className="font-semibold text-neutral-900">Upcoming shifts & breaks</p>{shifts.length ? <ul className="mt-3 space-y-3">{shifts.map((shift) => <li key={shift.id} className="rounded-lg bg-neutral-50 p-3"><p className="font-medium text-neutral-900">{shift.profile?.full_name || "Staff"} · {shift.classroom?.name || "Classroom"}</p><p className="mt-1 text-xs text-neutral-500">{new Date(shift.starts_at).toLocaleString()} – {new Date(shift.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>{(breakByShift[shift.id] || []).length ? <ul className="mt-2 space-y-1">{breakByShift[shift.id].map((item) => <li key={item.id} className="flex items-center justify-between text-xs text-neutral-700"><span>Break {new Date(item.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}–{new Date(item.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span><button type="button" aria-label="Remove scheduled break" disabled={busy === `break-delete-${item.id}`} onClick={() => onDelete(item.id)} className="rounded-full p-1 text-neutral-500 hover:bg-red-50 hover:text-red-700"><Trash size={13} weight="bold" /></button></li>)}</ul> : <p className="mt-2 text-xs text-neutral-500">No scheduled break</p>}</li>)}</ul> : <p className="mt-3 text-sm text-neutral-500">No future shifts are available. Add shifts in <Link to="/admin/scheduling" className="font-semibold text-primary-hover underline">Scheduling</Link>.</p>}</div>;
}
